import Image from "next/image";
import {
  ARCHIVE_FRAMES,
  SECTION_IDS,
  TERRAK_PILLARS,
  TERRAK_STATS,
  TERRAK_STEPS,
} from "./content";

export const Terrakio = () => (
  <section
    id={SECTION_IDS.terrakio}
    className="relative overflow-hidden px-[clamp(16px,4vw,64px)] py-[clamp(96px,13vw,200px)]"
  >
    <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_70%_30%,rgba(53,195,224,.1),transparent_64%)]" />

    <div className="dc-shell relative z-2">
      <div className="mb-[clamp(48px,6vw,80px)] grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] items-end gap-[clamp(32px,5vw,90px)]">
        <h2 className="m-0 text-[clamp(40px,7vw,124px)] font-bold leading-[.9] tracking-[-.045em]">
          THE LIVING
          <br />
          ARCHIVE
        </h2>
        <p className="m-0 max-w-[52ch] text-[clamp(16px,1.4vw,20px)] leading-[1.7] text-mist-2">
          Hundreds of satellite, climate and elevation sources, reconciled by our
          scientists and engineers into one coherent archive: aligned, validated,
          ready to be analysed. Decades of history, current to the latest
          observation.
        </p>
      </div>

      {/* Archive span */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-0.5">
        {ARCHIVE_FRAMES.map((frame) => (
          <figure
            key={frame.stage}
            className="group relative m-0 h-[clamp(200px,22vw,330px)] overflow-hidden"
          >
            <Image
              src={frame.img}
              alt={frame.alt}
              fill
              sizes="(max-width: 768px) 100vw, 33vw"
              className="object-cover saturate-[.85] transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(3,12,22,.1),rgba(3,12,22,.88))]" />
            <figcaption className="absolute inset-x-5 bottom-[18px]">
              <div className="dc-mono mb-2 text-[12px] uppercase tracking-[.2em] text-mint">
                {frame.stage}
              </div>
              <div className="text-[15px] leading-[1.45] text-paper-2">
                {frame.caption}
              </div>
            </figcaption>
          </figure>
        ))}
      </div>

      <div className="mt-[26px] mb-[clamp(48px,6vw,88px)]">
        <div className="relative h-px bg-[linear-gradient(90deg,rgba(255,255,255,.12),rgba(127,232,180,.75))]">
          <span className="absolute left-0 -top-[5px] h-[11px] w-px bg-white/30" />
          <span className="absolute left-1/3 -top-[5px] h-[11px] w-px bg-white/40" />
          <span className="absolute left-2/3 -top-[5px] h-[11px] w-px bg-mint/60" />
          <span className="absolute right-0 -top-2 h-[17px] w-[3px] bg-mint" />
        </div>
        <div className="dc-mono mt-3.5 flex justify-between text-[13px] tracking-[.16em] text-mist-6">
          <span>2001</span>
          <span>2010</span>
          <span>2020</span>
          <span className="text-mint">2026 · TODAY</span>
        </div>
        <div className="dc-mono mt-4 text-[12px] uppercase tracking-[.18em] text-mist-4">
          Archive span — the same place, re-read whenever the question is asked
        </div>
      </div>

      {/* Engine */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,330px),1fr))] items-start gap-[clamp(32px,4vw,64px)]">
        <div>
          <p className="mt-0 mb-[30px] text-[clamp(16px,1.4vw,20px)] leading-[1.7] text-mist-2">
            Terrak.io is the engine that brings the archive to life — one
            question, however large, answered in seconds and at full detail,
            delivered into the systems you already run.
          </p>

          <div className="grid">
            {TERRAK_STATS.map((stat) => (
              <div
                key={stat.label}
                className="flex items-baseline gap-5 border-t border-white/[.14] py-[18px]"
              >
                <div className="min-w-[120px] text-[clamp(26px,2.6vw,38px)] font-bold tracking-[-.03em] text-mint">
                  {stat.value}
                </div>
                <div className="dc-mono text-[12px] uppercase tracking-[.16em] text-mist-5">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          <a
            href="https://terrak.io"
            target="_blank"
            rel="noreferrer"
            className="mt-[30px] inline-flex items-center gap-2.5 text-[15px] font-semibold text-mint transition-colors hover:text-paper"
          >
            Open the Terrak.io playground <span className="dc-mono">→</span>
          </a>
        </div>

        <div className="border border-white/[.14] bg-ink/[.82]">
          <div className="dc-mono flex items-center gap-2.5 border-b border-white/[.12] px-[18px] py-[13px] text-[12px] tracking-[.16em] text-mist-5">
            <span className="h-[7px] w-[7px] bg-mint" />
            TERRAK.IO — QUERY
          </div>
          <div className="dc-mono p-6 text-[13px] leading-[2] text-mist-2">
            <div>
              <span className="text-mist-4">ask</span>{" "}
              <span className="text-mint">
                &quot;how much forest was lost here since 2020?&quot;
              </span>
            </div>
            <div>
              <span className="text-mist-4">area</span> cadastre.parcel_18842
            </div>
            <div>
              <span className="text-mist-4">data</span> sentinel-2 · lidar ·
              climate · <span className="text-cyan">your layers</span>
            </div>
            <div>
              <span className="text-mist-4">out</span> geotiff · json · map tiles
              · report
            </div>
          </div>
          <div className="grid gap-3.5 border-t border-white/[.12] p-6">
            {TERRAK_STEPS.map((step) => (
              <div
                key={step.label}
                className="grid grid-cols-[88px_minmax(0,1fr)] items-center gap-4"
              >
                <div className="dc-mono text-[11px] uppercase tracking-[.16em] text-mist-4">
                  {step.label}
                </div>
                <div className="flex h-6 items-center border-l-2 border-mint bg-[linear-gradient(90deg,rgba(127,232,180,.22),transparent)] px-3 text-[13px] text-paper">
                  {step.value}
                </div>
              </div>
            ))}
            <div className="mt-1.5 flex items-baseline gap-3.5 border-t border-white/[.12] pt-[18px]">
              <span className="dc-mono text-[11px] uppercase tracking-[.16em] text-mist-4">
                Answer
              </span>
              <span className="text-[22px] font-semibold tracking-[-.02em] text-mint">
                Seconds, not a project.
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-[clamp(60px,8vw,110px)] border-t border-white/[.14] pt-[clamp(36px,4vw,60px)]">
        <h3 className="mt-0 mb-[clamp(28px,3vw,44px)] text-[clamp(24px,2.8vw,40px)] font-semibold leading-[1.1] tracking-[-.028em]">
          One engine, every project.
        </h3>
        <div className="grid grid-cols-1 gap-[clamp(24px,3vw,56px)] sm:grid-cols-2 xl:grid-cols-4">
          {TERRAK_PILLARS.map((pillar) => (
            <div key={pillar.title} className="min-w-0">
              <h4 className="mt-0 mb-3 text-[19px] font-semibold tracking-[-.018em] text-paper">
                {pillar.title}
              </h4>
              <p className="m-0 text-base leading-[1.7] text-mist-3">
                {pillar.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
